/**
 * Group Thread Tags — CLIENT-side enablement (key 1 of 2).
 *
 * This module governs only whether the UI *asks* for tags. It is NOT the
 * authoritative gate. The server holds that:
 * `ops/groupThreadTags` (see `functions/src/prest0nSession/threadTagsEnablement.ts`)
 * decides per account whether the read/patch/list endpoints will answer at all,
 * and it can be flipped in ≤30s with no redeploy. See
 * `RUNBOOK-group-thread-tags-enablement-20260804.md`.
 *
 * Both keys must agree before a tag surface renders, so either one alone switches
 * the feature off — the client key covers "hide the UI", the server key covers
 * "refuse the data".
 *
 * DEFAULT OFF, fail-closed: the compiled constant is `false` and the account
 * allowlist ships empty, so merging and deploying activates nothing. Enabling is
 * a product-only (Malaika) decision.
 *
 * This flag is INDEPENDENT of the Group Threading shared gate
 * (`GROUP_THREADING_SHARED_GATE_ENABLED`) — Tags work must never flip that gate.
 */
export const GROUP_THREAD_TAGS_ENABLED: boolean = false;

export const GROUP_THREAD_TAGS_ACCOUNTS_ENV = 'REACT_APP_GROUP_THREAD_TAGS_ACCOUNTS';

let activeAccountId: string | null = null;

export function setGroupThreadTagsAccount(accountId: string | null | undefined): void {
  activeAccountId = (accountId ?? '').trim() || null;
}

function readEnabledTagAccountsFromEnv(env?: Record<string, string | undefined>): Set<string> {
  const raw = (env
    ? env[GROUP_THREAD_TAGS_ACCOUNTS_ENV]
    : process.env.REACT_APP_GROUP_THREAD_TAGS_ACCOUNTS) ?? '';
  return new Set(
    String(raw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

export interface GroupThreadTagsFlagContext {
  accountId?: string | null;
  override?: boolean;
  enabledAccountIds?: Iterable<string>;
  env?: Record<string, string | undefined>;
}

export function resolveGroupThreadTagsEnabled(ctx: GroupThreadTagsFlagContext = {}): boolean {
  if (ctx.override !== undefined) return ctx.override;
  if (GROUP_THREAD_TAGS_ENABLED) return true;
  const accountId = (ctx.accountId ?? activeAccountId ?? '').trim();
  if (!accountId) return false;
  const source = ctx.enabledAccountIds ?? readEnabledTagAccountsFromEnv(ctx.env);
  const set = source instanceof Set ? source : new Set(source);
  if (set.has('*')) return true;
  return set.has(accountId);
}

/**
 * Resolve the effective enabled state. Components accept an optional `enabled`
 * override (used by tests to exercise the on-path without shipping it on); when
 * omitted the module constant governs.
 */
export function isGroupThreadTagsEnabled(override?: boolean): boolean {
  return override === undefined ? resolveGroupThreadTagsEnabled() : override;
}
